import { Button, Menu, MenuItem, Popover, Position, Intent } from '@blueprintjs/core'
import { confirmDialog, lang, toast } from 'enablex/shared'
import React, { FC, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import api from '~/app/api'
import { AppState } from '~/app/rootReducer'

interface Department {
  id: number
  name: string
  email?: string
  status: 'active' | 'suspended'
  workspaceId: string
  createdAt: Date
  updatedAt: Date
}

interface Props extends ConnectedProps<typeof connector> {
  department: Department
  onDepartmentUpdated: () => void
}

const DepartmentActions: FC<Props> = props => {
  const { department } = props
  const [isLoading, setIsLoading] = useState(false)

  const suspendDepartment = async () => {
    if (
      !(await confirmDialog(
        `Are you sure you want to suspend department "${department.name}"?`,
        {
          acceptLabel: 'Suspend'
        }
      ))
    ) {
      return
    }

    setIsLoading(true)
    try {
      await api
        .getSecured()
        .post(`/admin/workspace/departments/${department.id}/suspend`)
      toast.success(`Department "${department.name}" suspended successfully`)
      props.onDepartmentUpdated()
    } catch (err) {
      toast.failure(`Failed to suspend department: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const activateDepartment = async () => {
    if (
      !(await confirmDialog(
        `Are you sure you want to activate department "${department.name}"?`,
        {
          acceptLabel: 'Activate'
        }
      ))
    ) {
      return
    }

    setIsLoading(true)
    try {
      await api
        .getSecured()
        .post(`/admin/workspace/departments/${department.id}/activate`)
      toast.success(`Department "${department.name}" activated successfully`)
      props.onDepartmentUpdated()
    } catch (err) {
      toast.failure(`Failed to activate department: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteDepartment = async () => {
    if (
      !(await confirmDialog(
        `Are you sure you want to delete department "${department.name}"? This action cannot be undone.`,
        {
          acceptLabel: 'Delete'
        }
      ))
    ) {
      return
    }

    setIsLoading(true)
    try {
      await api
        .getSecured()
        .delete(`/admin/workspace/departments/${department.id}`)
      toast.success(`Department "${department.name}" deleted successfully`)
      props.onDepartmentUpdated()
    } catch (err) {
      toast.failure(`Failed to delete department: ${err.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const menu = (
    <Menu>
      {department.status === 'active' ? (
        <MenuItem
          icon="pause"
          text="Suspend Department"
          onClick={suspendDepartment}
          disabled={isLoading}
        />
      ) : (
        <MenuItem
          icon="play"
          text="Activate Department"
          onClick={activateDepartment}
          disabled={isLoading}
        />
      )}
      <MenuItem
        icon="edit"
        text="Edit Department"
        onClick={() => {
          // TODO: Implement edit modal
          toast.info('Edit functionality coming soon')
        }}
        disabled={isLoading}
      />
      <MenuItem
        icon="trash"
        text="Delete Department"
        intent={Intent.DANGER}
        onClick={deleteDepartment}
        disabled={isLoading}
      />
    </Menu>
  )

  return (
    <Popover content={menu} position={Position.BOTTOM_RIGHT}>
      <Button
        icon="more"
        minimal
        small
        disabled={isLoading}
      />
    </Popover>
  )
}

const mapStateToProps = (state: AppState) => ({})

const connector = connect(mapStateToProps, {})

export default connector(DepartmentActions)
